import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { z } from 'zod'
import { apiFetch } from '@shared/api/http'

const RequirementSchema = z.object({
  id: z.string(),
  status: z.enum(['past_due', 'currently_due', 'pending_verification', 'eventually_due']),
  source: z.enum(['current', 'future']),
  deadline: z.string().nullable(),
  errorCode: z.string().nullable(),
  /** Profile form field that resolves it; null outside the form or when the form has no field for it. */
  field: z.string().nullable().catch(null),
  group: z.enum(['address', 'birth_date', 'document', 'people']).nullable().catch(null),
  /**
   * What the seller does: provide missing data, fix a refused value, check values that did not
   * match each other, wait while it is reviewed, wait for the company's people to be added in the
   * identity step, or a refused CPF/CNPJ that support handles.
   */
  resolution: z
    .enum([
      'provide',
      'fix',
      'check',
      'review',
      'awaiting_people',
      'awaiting_document',
      'document_refused',
    ])
    .catch('provide'),
})

const VerificationDetailsSchema = z.object({
  kycStatus: z.enum(['verified', 'pending_verification', 'action_required', 'rejected']),
  nextAction: z.enum(['none', 'provide_information', 'await_stripe_review', 'contact_support']),
  currentDeadline: z.string().nullable(),
  observedAt: z.string().nullable(),
  syncFailure: z.object({ at: z.string(), code: z.string() }).nullable(),
  attentionLevel: z.enum(['none', 'next_step', 'attention']).default('none'),
  /** Form fields the identity verification already confirmed; changing one restarts it. */
  verifiedFields: z.array(z.string()).default([]),
  requirements: z.object({
    form: z.array(RequirementSchema),
    embedded: z.array(RequirementSchema),
    support: z.array(RequirementSchema),
    bank_account: z.array(RequirementSchema),
  }),
})

const BankAccountSchema = z.object({
  id: z.string(),
  bankName: z.string().nullable(),
  last4: z.string().nullable(),
  status: z.enum(['new', 'validated', 'verified', 'verification_failed', 'errored', 'unknown']),
  role: z.enum(['active', 'pending_replacement', 'previous']),
})

const BankSchema = z.object({
  accounts: z.array(BankAccountSchema),
  readiness: z.object({
    ready: z.boolean(),
    reason: z
      .enum([
        'not_observed',
        'no_default_account',
        'default_account_errored',
        'default_account_verification_failed',
        'external_account_required',
        'payouts_disabled',
      ])
      .nullable(),
  }),
})

const EligibilityReasonSchema = z.enum([
  'connect_not_configured',
  'not_linked',
  'user_not_found',
  'status_unavailable',
  'kyc_pending_verification',
  'kyc_action_required',
  'kyc_rejected',
  'card_payments_not_active',
  'transfers_not_active',
  'bank_no_default_account',
  'bank_default_account_errored',
  'bank_default_account_verification_failed',
  'bank_external_account_required',
  'bank_payouts_disabled',
  'reserve_not_configured',
  'reserve_initial_pending',
  'reserve_initial_failed',
  'reserve_initial_dead',
])

const EligibilityCheckSchema = z.object({
  status: z.enum(['passed', 'failed', 'not_evaluated']),
  reasons: z.array(EligibilityReasonSchema),
})

const EligibilitySchema = z.object({
  approved: z.boolean(),
  reasons: z.array(EligibilityReasonSchema),
  checks: z.object({
    link: EligibilityCheckSchema,
    kyc: EligibilityCheckSchema,
    capabilities: EligibilityCheckSchema,
    bank: EligibilityCheckSchema,
    reserve: EligibilityCheckSchema,
  }),
  kycStatus: z.enum(['verified', 'pending_verification', 'action_required', 'rejected']).nullable(),
  observedAt: z.string().nullable(),
  syncFailure: z.object({ at: z.string(), code: z.string() }).nullable(),
})

export const IdentityRejectionSchema = z.object({
  field: z.string().nullable(),
  reason: z.enum(['missing', 'invalid', 'invalid_document', 'other']),
})

export type IdentityRejection = z.infer<typeof IdentityRejectionSchema>

const OnboardingSchema = z.object({
  badge: z.enum(['in_progress', 'in_review', 'correction_needed', 'approved']),
  steps: z.object({
    profile: z.enum(['pending', 'in_review', 'fix', 'done']),
    identity: z.enum(['unavailable', 'preparing', 'pending', 'in_review', 'fix', 'done']),
    bank: z.enum(['unavailable', 'pending', 'fix', 'done']),
  }),
  operations: z.object({
    sales: z.enum(['waiting', 'in_review', 'released', 'blocked']),
    payouts: z.enum(['waiting', 'in_review', 'released', 'blocked']),
  }),
  blockedBy: z
    .object({
      sales: z.enum(['vendepay', 'stripe']).nullable(),
      payouts: z.enum(['vendepay', 'stripe']).nullable(),
    })
    .default({ sales: null, payouts: null }),
  nextStep: z.object({
    owner: z.enum(['seller', 'stripe', 'support', 'none']),
    code: z.enum([
      'save_profile',
      'fix_field',
      'identity_document',
      'add_bank_account',
      'fix_bank_account',
      'wait_review',
      'rejected',
      'activation_failed',
      'unsupported_requirement',
      'account_disabled',
      'sync_failing',
      'none',
    ]),
    requirementId: z.string().optional(),
    since: z.string().optional(),
  }),
  /** What Stripe is checking across the account (company, owners, documents, ...). */
  reviewing: z.array(z.string()).optional().catch(undefined),
  /** Why the identity step asks for a document that settles data Stripe could not match. */
  identityReason: z
    .enum(['representative_authority', 'data_not_confirmed'])
    .nullable()
    .optional()
    .catch(null),
})

export const KycVerificationSchema = z.object({
  linked: z.boolean(),
  onboarding: OnboardingSchema,
  bank: BankSchema.nullable(),
  eligibility: EligibilitySchema,
  submission: z.object({
    state: z.enum(['not_linked', 'not_submitted', 'pending', 'failed', 'accepted']),
    last: z
      .object({
        id: z.string(),
        status: z.enum(['pending', 'accepted', 'failed']),
        actorType: z.enum(['participant', 'staff', 'system']),
        createdAt: z.string(),
        completedAt: z.string().nullable(),
        failureCode: z.string().nullable(),
        failureParam: z.string().nullable(),
      })
      .nullable(),
    lastAcceptedAt: z.string().nullable(),
    /** A field refused when the account was created; the saved profile still holds that value. */
    rejectedField: IdentityRejectionSchema.nullable().default(null),
  }),
  verification: VerificationDetailsSchema.nullable(),
})

export type KycVerification = z.infer<typeof KycVerificationSchema>
export type KycRequirement = z.infer<typeof RequirementSchema>
export type EligibilityReasonCode = z.infer<typeof EligibilityReasonSchema>
export type KycOnboarding = z.infer<typeof OnboardingSchema>

const verificationKey = ['kyc', 'verification'] as const

export const verificationKeys = { view: verificationKey } as const

export async function fetchKycVerification(): Promise<KycVerification> {
  return KycVerificationSchema.parse(await apiFetch<unknown>('/kyc/verification'))
}

export async function syncKycVerification(): Promise<KycVerification> {
  return KycVerificationSchema.parse(
    await apiFetch<unknown>('/kyc/verification/sync', { method: 'POST', body: {} }),
  )
}

// Right after the account is created its state may not be observed yet; poll briefly
// (about a minute) so the identity step becomes available without a reload.
const UNOBSERVED_POLL_MS = 3_000
const UNOBSERVED_POLL_LIMIT = 20
const ANALYSIS_POLL_MS = 20_000
const ANALYSIS_POLL_LIMIT_MS = 15 * 60 * 1_000

export function useKycVerification(enabled = true) {
  const [pageVisitStartedAt] = useState(() => Date.now())
  return useQuery({
    queryKey: verificationKey,
    queryFn: fetchKycVerification,
    enabled,
    refetchInterval: (query) =>
      query.state.data?.linked === true &&
      query.state.data.verification === null &&
      query.state.dataUpdateCount < UNOBSERVED_POLL_LIMIT
        ? UNOBSERVED_POLL_MS
        : query.state.data?.onboarding.badge === 'in_review' &&
            Date.now() - pageVisitStartedAt < ANALYSIS_POLL_LIMIT_MS
          ? ANALYSIS_POLL_MS
          : false,
  })
}

/**
 * Pulls the account state from the provider before reading the view, for "Atualizar status":
 * a plain refetch only re-reads what the last webhook stored. The server throttles this per user.
 */
export function useSyncKycVerification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: syncKycVerification,
    onSuccess: (view) => queryClient.setQueryData(verificationKey, view),
  })
}

export type BankAccount = z.infer<typeof BankAccountSchema>
export type BankAccountReadiness = z.infer<typeof BankSchema>['readiness']
export type AttachBankAccountInput = {
  bankCode: string
  branch: string
  accountNumber: string
}

export function useAttachBankAccount() {
  return useBankAccountMutation((input: AttachBankAccountInput) =>
    apiFetch<unknown>('/kyc/bank-accounts', { method: 'POST', body: input }),
  )
}

export function useMakeDefaultBankAccount() {
  return useBankAccountMutation((bankAccountId: string) =>
    apiFetch<unknown>(`/kyc/bank-accounts/${encodeURIComponent(bankAccountId)}/default`, {
      method: 'POST',
      body: {},
    }),
  )
}

/**
 * Bank routes answer with the full verification view. Any failure may still have changed Stripe
 * state (a failed switch leaves the new account attached), so errors refetch the view.
 */
function useBankAccountMutation<TInput>(request: (input: TInput) => Promise<unknown>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: TInput) => KycVerificationSchema.parse(await request(input)),
    onSuccess: (value) => {
      queryClient.setQueryData(verificationKey, value)
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: verificationKey })
    },
  })
}

const OnboardingSessionSchema = z.object({
  clientSecret: z.string().min(1),
  publishableKey: z.string().min(1),
  embeddedRequirements: z.array(z.string()),
})

export type KycOnboardingSession = z.infer<typeof OnboardingSessionSchema>

export async function createKycOnboardingSession(): Promise<KycOnboardingSession> {
  return OnboardingSessionSchema.parse(
    await apiFetch<unknown>('/kyc/onboarding-session', { method: 'POST', body: {} }),
  )
}
